import styled, { css } from "styled-components";
import { getCornerRadius } from "@swiggy-private/dls-web-common/dist/components/themes/CornerRadiusMapping/mapping";

interface InputFieldContainerProps {
    $inline?: boolean;
}

interface InputProps {
    $showFocusBar?: boolean;
    $error?: boolean;
    $warning?: boolean;
    $disabled?: boolean;
}

interface LabelProps {
    $isActive?: boolean;
    $error?: boolean;
    $warning?: boolean;
    $disabled?: boolean;
}

interface MessageContainerProps {
    $showFocusBar?: boolean;
}

export const InputFieldContainer = styled.div<InputFieldContainerProps>`
    margin: ${props => props.theme.spacing.xLarge} ${props => props.theme.spacing.empty};
    padding: ${props => props.theme.spacing.empty};
    display: ${props => (props.$inline ? "inline-block" : "block")};
    position: relative;

    ${props =>
        props.$inline &&
        css`
            vertical-align: middle;
        `}
`;

export const InputWrapper = styled.div`
    position: relative;
`;

export const StyledInput = styled.input<InputProps>`
    background: ${props => props.theme.colors[props.theme.colorMode].background_Primary};
    padding: ${props => props.theme.spacing.empty} ${props => props.theme.spacing.medium};
    outline: none;
    height: 56px;
    width: 100%;
    margin: ${props => props.theme.spacing.empty};
    box-shadow: none;
    box-sizing: border-box;
    caret-color: ${props => props.theme.colors[props.theme.colorMode].primary};
    ${props => props.theme.typography["V2.body1.neutral.medium"]}
    color: ${props => props.theme.colors[props.theme.colorMode].text_Highest_Emphasis};

    ${props =>
        props.$showFocusBar
            ? css`
                  border: none;
                  border-bottom: 1px solid ${props.theme.colors[props.theme.colorMode].border_Secondary};
                  border-radius: ${props.theme.cornerRadius.none};
              `
            : css`
                  border: 1px solid ${props.theme.colors[props.theme.colorMode].border_Secondary};
                  border-radius: ${getCornerRadius(props.theme, "small")};
              `}

    &::placeholder,
    &::-webkit-input-placeholder {
        ${props => props.theme.typography["V2.body1.neutral.medium"]}
        color: ${props => props.theme.colors[props.theme.colorMode].text_Med_Emphasis};
    }

    &:hover,
    &:focus,
    &:disabled {
        background: ${props => props.theme.colors[props.theme.colorMode].background_Primary};
    }

    &:focus {
        border-width: ${props => props.theme.spacing.xxxSmall};
        ${props =>
            props.$showFocusBar
                ? css`
                      border-bottom-color: ${props.theme.colors[props.theme.colorMode].primary};
                  `
                : css`
                      border-color: ${props.theme.colors[props.theme.colorMode].primary};
                  `}

        + label {
            color: ${props => props.theme.colors[props.theme.colorMode].primary};
        }
    }

    ${props =>
        props.$disabled &&
        css`
            color: ${props.theme.colors[props.theme.colorMode].text_Low_Emphasis};
            border-color: ${props.theme.colors[props.theme.colorMode].border_Secondary};
            border-width: 1px;
        `}

    ${props =>
        props.$warning &&
        css`
            border-width: ${props => props.theme.spacing.xxxSmall};
            ${props.$showFocusBar
                ? css`
                      border-bottom-color: ${props.theme.colors[props.theme.colorMode].warning};
                  `
                : css`
                      border-color: ${props.theme.colors[props.theme.colorMode].warning};
                  `}

            &:focus {
                border-width: ${props => props.theme.spacing.xxxSmall};
                ${props.$showFocusBar
                    ? css`
                          border-bottom-color: ${props.theme.colors[props.theme.colorMode].warning};
                      `
                    : css`
                          border-color: ${props.theme.colors[props.theme.colorMode].warning};
                      `}

                + label {
                    color: ${props.theme.colors[props.theme.colorMode].warning};
                }
            }
        `}
    
    ${props =>
        props.$error &&
        css`
            border-width: ${props => props.theme.spacing.xxxSmall};
            ${props.$showFocusBar
                ? css`
                      border-bottom-color: ${props.theme.colors[props.theme.colorMode].negative};
                  `
                : css`
                      border-color: ${props.theme.colors[props.theme.colorMode].negative};
                  `}

            &:focus {
                border-width: ${props => props.theme.spacing.xxxSmall};
                ${props.$showFocusBar
                    ? css`
                          border-bottom-color: ${props.theme.colors[props.theme.colorMode].negative};
                      `
                    : css`
                          border-color: ${props.theme.colors[props.theme.colorMode].negative};
                      `}

                + label {
                    color: ${props.theme.colors[props.theme.colorMode].negative};
                }
            }
        `}
`;

export const Label = styled.label<LabelProps>`
    ${props => props.theme.typography["V2.body1.neutral.regular"]}
    color: ${props => props.theme.colors[props.theme.colorMode].text_Med_Emphasis};
    position: absolute;
    bottom: ${props => props.theme.spacing.medium};
    left: ${props => props.theme.spacing.medium};
    cursor: text;
    transition: 0.2s ease;
    will-change: transform, color, font-size, top, bottom;

    ${props =>
        props.$isActive &&
        css`
            ${props => props.theme.typography["V2.body3.neutral.regular"]}
            top: ${props => props.theme.spacing.empty};
            bottom: unset;
            background: ${props.theme.colors[props.theme.colorMode].background_Primary};
            padding: ${props => props.theme.spacing.empty} ${props => props.theme.spacing.xxSmall};
            transform: translate3d(0, -50%, 0);
        `}

    ${props =>
        props.$error &&
        css`
            color: ${props.theme.colors[props.theme.colorMode].negative};
        `}
    
    ${props =>
        props.$warning &&
        css`
            color: ${props.theme.colors[props.theme.colorMode].warning};
        `}
    
    ${props =>
        props.$disabled &&
        css`
            color: ${props.theme.colors[props.theme.colorMode].text_Low_Emphasis};
        `}
`;

export const ErrorIconSvg = styled.svg`
    position: absolute;
    top: 50%;
    right: ${props => props.theme.spacing.medium};
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
`;

export const ErrorContainer = styled.div<MessageContainerProps>`
    color: ${props => props.theme.colors[props.theme.colorMode].negative};
    ${props =>
        props.$showFocusBar
            ? css`
                  padding: ${props => props.theme.spacing.small} ${props => props.theme.spacing.empty}
                      ${props => props.theme.spacing.xxSmall};
              `
            : css`
                  padding: ${props => props.theme.spacing.xLarge} ${props => props.theme.spacing.medium}
                      ${props => props.theme.spacing.xxSmall};
                  margin-top: calc(${props => props.theme.spacing.large} * -1);
                  background: ${props.theme.colors[props.theme.colorMode].negative_Sub};
                  border: 1.5px solid ${props.theme.colors[props.theme.colorMode].border_Tertiary};
                  border-radius: ${getCornerRadius(props.theme, "small")};

                  + div {
                      margin-bottom: ${props => props.theme.spacing.empty};
                  }
              `}
`;

export const WarningContainer = styled.div<MessageContainerProps>`
    ${props =>
        props.$showFocusBar
            ? css`
                  padding: ${props => props.theme.spacing.small} ${props => props.theme.spacing.empty}
                      ${props => props.theme.spacing.xxSmall};
              `
            : css`
                  padding: ${props => props.theme.spacing.xLarge} ${props => props.theme.spacing.medium}
                      ${props => props.theme.spacing.xxSmall};
                  margin-top: calc(${props => props.theme.spacing.large} * -1);
                  background: ${props.theme.colors[props.theme.colorMode].warning_Sub};
                  border-radius: ${getCornerRadius(props.theme, "small")};

                  + div {
                      margin-bottom: ${props => props.theme.spacing.empty};
                  }
              `}
`;
